import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { beginPasswordLogin, isGoogleLoginConfigured } from '../../auth/cognito';
import { accountContinuation, captureContinuation, continueToAccountSite } from '../../auth/continuation';
import { requestEmailVerification } from '../../auth/memberApi';
import './auth.css';

const Login = () => {
  const { completeGoogleLogin, isAuthenticated, isLoading, loginWithGoogle } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const callback = useRef(false);
  const automaticLogin = useRef(false);
  const query = new URLSearchParams(location.search);
  const hasCallback = query.has('code') || query.has('error');
  useEffect(() => { captureContinuation(location.search); }, [location.search]);
  useEffect(() => {
    if (!hasCallback || callback.current) return;
    callback.current = true;
    automaticLogin.current = true;
    setBusy(true);
    completeGoogleLogin(new URLSearchParams(location.search)).then(completed => {
      if (completed && !continueToAccountSite()) {
        const destination = sessionStorage.getItem('hogcloud-login-return');
        sessionStorage.removeItem('hogcloud-login-return');
        navigate(destination?.startsWith('/') && !destination.startsWith('//') && !/[\\\r\n]/.test(destination) ? destination : '/welcome', { replace: true });
      }
    }).catch(cause => {
      setError(cause instanceof Error ? cause.message : 'Unable to complete login.');
      navigate('/login', { replace: true });
    }).finally(() => { callback.current = false; setBusy(false); });
  }, [completeGoogleLogin, hasCallback, location.search, navigate]);
  useEffect(() => {
    if (!isAuthenticated || isLoading || hasCallback || busy) return;
    if (!continueToAccountSite()) {
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from?.startsWith('/') && !from.startsWith('//') && !from.includes('\\') ? from : '/welcome', { replace: true });
    }
  }, [isAuthenticated, isLoading, hasCallback, busy, location.state, navigate]);
  const start = (google = false) => {
    const from = (location.state as { from?: string } | null)?.from;
    if (from?.startsWith('/') && !from.startsWith('//') && !/[\\\r\n]/.test(from)) sessionStorage.setItem('hogcloud-login-return', from);
    setBusy(true); setError('');
    (google ? loginWithGoogle() : beginPasswordLogin(email)).catch(cause => {
      setError(cause instanceof Error ? cause.message : 'Unable to start login.');
      setBusy(false);
    });
  };
  useEffect(() => {
    if (isLoading || isAuthenticated || hasCallback || automaticLogin.current || !accountContinuation()) return;
    automaticLogin.current = true;
    setBusy(true);
    beginPasswordLogin().catch(cause => {
      setError(cause instanceof Error ? cause.message : 'Unable to start login.');
      setBusy(false);
    });
  }, [isLoading, isAuthenticated, hasCallback]);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true); setError(''); setMessage('');
    try {
      const verification = await requestEmailVerification(email);
      if (verification.verificationRequired) {
        setMessage('Check your email for verification code');
        setBusy(false);
        return;
      }
      start();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to start login.');
      setBusy(false);
    }
  };
  return <section className="auth-shell">
    <div className="auth-hero"><p>Member access</p><h1>Welcome back.</h1>
      <span>One Hogcloud account for Hogcloud and Device Hog.</span></div>
    <div className="auth-card">
      {error && <p className="auth-message error" role="alert">{error}</p>}
      {message && <p className="auth-message success" role="status">{message}</p>}
      <form className="auth-form" onSubmit={submit}>
        <div className="auth-field"><label htmlFor="login-email">Email</label>
          <input id="login-email" type="email" autoComplete="email" required value={email} onChange={event => setEmail(event.target.value)} />
        </div>
        <button className="auth-submit" type="submit" disabled={busy || isLoading}>{busy ? 'Connecting…' : 'Login'}</button>
      </form>
      {isGoogleLoginConfigured && <button className="auth-google" type="button" disabled={busy || isLoading} onClick={() => start(true)}>Continue with Google</button>}
      <p className="auth-switch">New here? <Link to="/enroll">Enroll</Link></p>
      <p className="auth-switch"><Link to="/reset-password">Reset password</Link></p>
    </div>
  </section>;
};
export default Login;
